import { Component, inject } from '@angular/core';
import { catchError, Observable, switchMap } from 'rxjs';
import { GlobalMessageType, StoreFinderSearchPage } from '@boels/models';
import { GlobalMessageFacade, StoreFinderFacade } from '@boels/shared-state';
import { Params } from '@angular/router';
import { StoreFinderSearchComponent } from '../store-finder-search/store-finder-search.component';
import { TranslocoModule } from '@jsverse/transloco';
import { StoreFinderListMapComponent } from '../store-finder-list-map/store-finder-list-map.component';
import { RoutingService } from '@boels/helpers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'boels-store-finder-search-result',
  templateUrl: './store-finder-search-result.component.html',
  styleUrls: ['./store-finder-search-result.component.scss'],
  imports: [
    StoreFinderSearchComponent,
    TranslocoModule,
    StoreFinderListMapComponent,
  ],
})
export class StoreFinderSearchResultComponent {
  #storeFinderFacade = inject(StoreFinderFacade);
  #routingService = inject(RoutingService);
  #globalMessageFacade = inject(GlobalMessageFacade);

  stores$: Observable<StoreFinderSearchPage> = this.#routingService
    .getRoutePropertyAsObservable('queryParams')
    .pipe(
      takeUntilDestroyed(),
      switchMap((queryParams: Params | undefined) => {
        const specialism = queryParams?.['specialism'];
        const query = queryParams?.['query'];
        const useMyLocation = queryParams?.['useMyLocation'] === 'true';

        // If `useMyLocation` is false, directly call `getPointOfServicesByQuery` on the `storeFinderFacade`.
        if (!useMyLocation) {
          return this.#storeFinderFacade.getPointOfServicesByQuery(
            query,
            undefined,
            specialism,
          );
        }

        // Attempt to get the user's geolocation. If successful, call `getPointOfServicesByGeoPoint`.
        // If geolocation fails, call `getPointOfServicesByQuery`.
        return this.getUserLocation().pipe(
          switchMap((geolocationPosition) => {
            const geoPoint = {
              longitude: geolocationPosition.coords.longitude,
              latitude: geolocationPosition.coords.latitude,
            };
            return this.#storeFinderFacade.getPointOfServicesByGeoPoint(
              geoPoint,
              undefined,
              specialism,
            );
          }),
          catchError(() =>
            this.#storeFinderFacade.getPointOfServicesByQuery(
              query,
              undefined,
              specialism,
            ),
          ),
        );
      }),
    );

  getUserLocation(): Observable<GeolocationPosition> {
    return new Observable((observer) => {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          observer.next(position);
          observer.complete();
        },
        (error: GeolocationPositionError) => {
          observer.error(error);

          this.#globalMessageFacade.add(
            { key: 'result.geolocationNotEnabled', scope: 'storeFinder' },
            GlobalMessageType.MSG_TYPE_ERROR,
          );
        },
      );
    });
  }
}
